import React, { useState } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { ArrowLeft, Save, UserPlus } from 'lucide-react'

export default function PortalAddPlayer() {
  const { team, canManage } = useOutletContext()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    jersey_number: '',
    position: '',
    date_of_birth: '',
    gender: '',
    medical_notes: '',
    allergies: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  })

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const payload = { ...form, team_id: team.team_id }
    // Remove empty fields
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') delete payload[k]
    })

    try {
      await portalRequest('/api/players', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      navigate(`/portal/team/${team.team_id}/roster`)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!canManage) {
    return (
      <div className="text-center py-20 text-zinc-400">
        You don't have permission to add players.
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button
        onClick={() => navigate(`/portal/team/${team.team_id}/roster`)}
        className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Roster</span>
      </button>

      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <UserPlus className="w-6 h-6 text-green-400" />
          Add Player
        </h1>
        <p className="text-zinc-400 mt-1">Add a new player to the team roster</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/10 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>
        )}

        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6 space-y-5">
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Basic Info</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">First Name *</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => update('first_name', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Last Name *</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => update('last_name', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Jersey #</label>
              <input
                type="text"
                value={form.jersey_number}
                onChange={(e) => update('jersey_number', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Position</label>
              <input
                type="text"
                value={form.position}
                onChange={(e) => update('position', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Date of Birth</label>
              <input
                type="date"
                value={form.date_of_birth}
                onChange={(e) => update('date_of_birth', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Gender</label>
              <select
                value={form.gender}
                onChange={(e) => update('gender', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              >
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6 space-y-5">
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Medical & Emergency</h3>
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1">Medical Notes</label>
            <textarea
              value={form.medical_notes}
              onChange={(e) => update('medical_notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none"
              placeholder="Any medical conditions..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1">Allergies</label>
            <input
              type="text"
              value={form.allergies}
              onChange={(e) => update('allergies', e.target.value)}
              className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Known allergies..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Emergency Contact Name</label>
              <input
                type="text"
                value={form.emergency_contact_name}
                onChange={(e) => update('emergency_contact_name', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Emergency Contact Phone</label>
              <input
                type="tel"
                value={form.emergency_contact_phone}
                onChange={(e) => update('emergency_contact_phone', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50 transition-all"
          >
            {loading ? (
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              <Save className="w-5 h-5" />
            )}
            Add Player
          </button>
          <button
            type="button"
            onClick={() => navigate(`/portal/team/${team.team_id}/roster`)}
            className="px-6 py-3 border border-zinc-700 text-zinc-300 rounded-xl font-medium hover:bg-zinc-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
